import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useAuthStore } from '@/stores/auth';
import { useAnalytics } from './AnalyticsProvider';
import { setTutorialActive } from '@/lib/tutorials/types/window';
import { useApiClient } from './ApiClientProvider';
import { useQueryClient } from '@tanstack/react-query';
import { cleanupTutorialResourcesSafe } from '@/lib/tutorials/cleanup';

/**
 * User tutorial preferences (stored in user preferences)
 * This is different from the engine’s UserTutorialState which tracks the state machine
 */
interface UserTutorialState {
  /** Whether the tutorial has been completed */
  completed: boolean;

  /** Whether the tutorial was skipped */
  skipped: boolean;

  /** If true, never show this tutorial again */
  neverShowAgain: boolean;

  /** Index of the last step the user was on */
  lastStepIndex: number;

  /** Timestamp when this state was last updated */
  lastUpdated?: string;
}

interface TutorialContextValue {
  /** ID of the currently active tutorial, or null if none */
  activeTutorialId: string | null;

  /** Current step index within the active tutorial */
  currentStepIndex: number;

  /** Total number of steps in the active tutorial */
  totalSteps: number;

  /** Start a tutorial by its ID */
  startTutorial: (tutorialId: string, totalSteps: number) => void;

  /** Advance to the next step */
  nextStep: () => void;

  /** Skip the tutorial (optionally mark as "never show again") */
  skipTutorial: (neverShowAgain: boolean) => Promise<void>;

  /** Complete the tutorial successfully */
  completeTutorial: () => Promise<void>;

  /** Get the state of a specific tutorial */
  getTutorialState: (tutorialId: string) => UserTutorialState | null;

  /** Check if a tutorial should be shown based on preferences */
  shouldShowTutorial: (tutorialId: string) => boolean;
}

const TutorialContext = createContext<TutorialContextValue | null>(null);

interface TutorialProviderProps {
  children: ReactNode;
}

export function TutorialProvider({ children }: TutorialProviderProps) {
  const { user, setUser } = useAuthStore();
  const { track } = useAnalytics();
  const api = useApiClient();
  const queryClient = useQueryClient();

  const [activeTutorialId, setActiveTutorialId] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);

  // Sync window.__tutorialActive flag with activeTutorialId state
  // This ensures window flag is always consistent with React state
  useEffect(() => {
    setTutorialActive(activeTutorialId !== null);
  }, [activeTutorialId]);

  // Extract tutorial preferences from user preferences (memoized to prevent unnecessary re-renders)
  // User preferences structure: { tutorials: { [tutorialId]: UserTutorialState } }
  const tutorialsMap = useMemo(
    () => (user?.preferences?.tutorials as Record<string, UserTutorialState>) || {},
    [user?.preferences?.tutorials]
  );

  const getTutorialState = useCallback(
    (tutorialId: string): UserTutorialState | null => {
      return tutorialsMap[tutorialId] || null;
    },
    [tutorialsMap]
  );

  const shouldShowTutorial = useCallback(
    (tutorialId: string): boolean => {
      const state = getTutorialState(tutorialId);
      if (!state) {
        return true; // Never seen before, should show
      }

      // Don’t show if user has completed, skipped, or explicitly said "never show again"
      // Tutorial should only show once per user, not once per assignment
      return !state.completed && !state.skipped && !state.neverShowAgain;
    },
    [getTutorialState]
  );

  const updateTutorialState = useCallback(
    async (tutorialId: string, updates: Partial<UserTutorialState>, createIfMissing = false) => {
      const currentState = getTutorialState(tutorialId);

      // Don’t create new state unless explicitly requested
      // This prevents resetting neverShowAgain when user preferences aren’t loaded yet
      if (!currentState && !createIfMissing) {
        console.warn("[TutorialProvider] Attempted to update tutorial state that doesn’t exist:", tutorialId);
        return;
      }

      // Add timestamp to updates
      const updatesWithTimestamp = {
        ...updates,
        lastUpdated: new Date().toISOString(),
      };

      // Use atomic backend endpoint to prevent race conditions
      // The backend merges updates server-side, preventing read-modify-write races
      try {
        const updatedUser = await api.updateTutorialPreferences(tutorialId, updatesWithTimestamp);

        // Update local state with the new user data
        setUser(updatedUser);

        // Also update React Query cache
        queryClient.setQueryData(['current-user'], updatedUser);
      } catch (error: unknown) {
        console.error('Failed to update tutorial preferences:', error);
        // Rethrow error so callers can handle it (prevents state transition on failure)
        throw error;
      }
    },
    [api, getTutorialState, queryClient, setUser]
  );

  const startTutorial = useCallback(
    (tutorialId: string, steps: number) => {
      setActiveTutorialId(tutorialId);
      setTotalSteps(steps);

      // Always start from beginning on page load
      // (Resume functionality removed per user request - refresh should restart)
      setCurrentStepIndex(0);

      track('tutorial_started', {
        tutorialId,
        stepIndex: 0,
      });

      // Update state to track that tutorial was started
      // Don’t create if missing - prevents resetting neverShowAgain when prefs aren’t loaded
      void updateTutorialState(tutorialId, {
        lastStepIndex: 0,
      }, false); // Don’t create if missing
    },
    [track, updateTutorialState]
  );

  const nextStep = useCallback(() => {
    if (!activeTutorialId) return;

    const nextIndex = currentStepIndex + 1;
    setCurrentStepIndex(nextIndex);

    track('tutorial_step_completed', {
      tutorialId: activeTutorialId,
      stepIndex: currentStepIndex,
    });

    // Update last step index
    void updateTutorialState(activeTutorialId, {
      lastStepIndex: nextIndex,
    });
  }, [activeTutorialId, currentStepIndex, track, updateTutorialState]);

  const skipTutorial = useCallback(
    async (neverShowAgain: boolean) => {
      if (!activeTutorialId) return;

      track('tutorial_skipped', {
        tutorialId: activeTutorialId,
        stepIndex: currentStepIndex,
        neverShowAgain,
      });

      // AWAIT backend update - don’t transition state until it succeeds
      // This prevents tutorial restart loop bug where UI shows "inactive" but backend never saved
      try {
        await updateTutorialState(activeTutorialId, {
          skipped: true,
          neverShowAgain,
          lastStepIndex: 0, // Reset progress when skipping
        }, true); // Create if missing - user explicitly skipped

        // Only transition state AFTER backend confirms success
        setActiveTutorialId(null);
        setCurrentStepIndex(0);
        setTotalSteps(0);
      } catch (error) {
        console.error('[TutorialProvider] Failed to save skip state:', error);

        // Delete backend tutorial resources even though state update failed
        // For skip, we CAN invalidate caches since user is dismissing the tutorial
        // (unlike complete, where user might be viewing tutorial resources in a modal)
        try {
          await cleanupTutorialResourcesSafe(api, queryClient, {
            invalidateCaches: true, // Safe to invalidate - user dismissed tutorial
            clearState: false, // Don’t clear window state - tutorial still active
          });
        } catch (cleanupError) {
          console.error('[TutorialProvider] Cleanup after skip failure also failed:', cleanupError);
        }

        // Don’t transition state - tutorial remains active so user can retry
        // Rethrow error so callers know the operation failed
        throw error;
      }
    },
    [activeTutorialId, currentStepIndex, track, updateTutorialState, api, queryClient]
  );

  const completeTutorial = useCallback(async () => {
    if (!activeTutorialId) return;

    track('tutorial_completed', {
      tutorialId: activeTutorialId,
      totalSteps,
    });

    // AWAIT backend update - don’t transition state until it succeeds
    // This prevents tutorial restart loop bug where UI shows "inactive" but backend never saved
    try {
      await updateTutorialState(activeTutorialId, {
        completed: true,
        lastStepIndex: totalSteps - 1,
      }, true); // Create if missing - user explicitly completed

      // Only transition state AFTER backend confirms success
      setActiveTutorialId(null);
      setCurrentStepIndex(0);
      setTotalSteps(0);
    } catch (error) {
      console.error('[TutorialProvider] Failed to save completion state:', error);

      // Delete backend tutorial resources even though state update failed
      // BUT don’t invalidate caches - user might still be viewing the markup modal
      // Cache invalidation will happen when the modal closes
      try {
        await cleanupTutorialResourcesSafe(api, queryClient, {
          invalidateCaches: false, // Don’t invalidate - user might be in modal
          clearState: false, // Don’t clear window state - tutorial still active
        });
      } catch (cleanupError) {
        console.error('[TutorialProvider] Cleanup after completion failure also failed:', cleanupError);
      }

      // Don’t transition state - tutorial remains active so user can retry
      // Rethrow error so callers know the operation failed
      throw error;
    }
  }, [activeTutorialId, totalSteps, track, updateTutorialState, api, queryClient]);

  const value = useMemo<TutorialContextValue>(
    () => ({
      activeTutorialId,
      currentStepIndex,
      totalSteps,
      startTutorial,
      nextStep,
      skipTutorial,
      completeTutorial,
      getTutorialState,
      shouldShowTutorial,
    }),
    [
      activeTutorialId,
      currentStepIndex,
      totalSteps,
      startTutorial,
      nextStep,
      skipTutorial,
      completeTutorial,
      getTutorialState,
      shouldShowTutorial,
    ]
  );

  return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>;
}

export function useTutorialContext(): TutorialContextValue {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorialContext must be used within TutorialProvider');
  }
  return context;
}
